import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { ContentHeaderKey } from "src/app/constants/content-header";
import { TableColumnType } from "src/app/constants/table";
import { AgentCompaniesSelected, AgentCompany, AgentFilterPayload, AgentUserFilterPayload, SupervisedAgent, SupervisedAgentList } from "src/app/models/agent";
import { Broker, WorkingBroker } from "src/app/models/broker";
import { TableColumn } from "src/app/models/table";

import { AgentService } from "src/app/services/agent.service";
import { FormValidationService } from "src/app/services/form-validation.service";
import { SessionService } from "src/app/services/storage/session.service";

@Component({
    selector: 'mwp-manage-agent-assign',
    templateUrl: './agent-assign.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgentAssignComponent implements OnInit {
    public brokers: Broker[];
    public agents: SupervisedAgent[] = [];

    public agentControlForm: FormGroup;
    public selectedAgentId: number;
    public isAssign: boolean;

    public companyForm: FormGroup;
    public companyFormArrayName: string = 'companyFormArray';
    public formSelectCheckbox: string = 'isSelected';
    public companyMatTableData: MatTableDataSource<AgentCompany>;
    public apiUrl = ContentHeaderKey.CounselorAgentAssign;
    public companyColumns: TableColumn[];
    public baseCompanyColumns: TableColumn[] = [
        {
            columnName: 'Company Name',
            propertyId: 'companyName',
            columnType: TableColumnType.label
        },
        {
            columnName: 'Company ID',
            propertyId: 'companyID',
            columnType: TableColumnType.label
        },
        {
            columnName: 'Code',
            propertyId: 'code',
            columnType: TableColumnType.label
        },
    ];

    constructor(
        private agentService: AgentService,
        private formBuilder: FormBuilder,
        private sessionService: SessionService,
    ) {
    }

    ngOnInit(): void {
        this.agentControlForm = this.formBuilder.group({
            brokerId: [this.sessionService.session.selectedBrokerId, Validators.required],
            agentID: [null, Validators.required]
        });

        this.agentService.getBrokers().subscribe((response: Broker[]) => {
            this.brokers = response;
        });

        this.setBroker(this.sessionService.session.selectedBrokerId);
    }

    public brokerChange(event: number): void {
        this.setBroker(event);
    }

    public setBroker(id: number): void {
        let l_WorkingBroker = new WorkingBroker();
        l_WorkingBroker.Id = id;

        const l_AgentFilterPayload: AgentFilterPayload = {
            agentID: this.sessionService.session.agent.agentID,
        };

        this.agentService.setWorkingBroker(l_WorkingBroker).subscribe(() => {
            this.agentControlForm.get('agentID').setValue(null);
            this.agents = null;
            this.companyForm = null;
            this.companyMatTableData = null;
            this.selectedAgentId = null;
            this.isAssign = false;
            
            this.agentService.getSupervisedAgentsFromSetByWorkingBroker(l_AgentFilterPayload).subscribe((response: SupervisedAgentList) => {
                this.agents = response.supervisedAgents;
            });
        });
    }

    public agentChange(event: number): void {
        this.selectedAgentId = event;
        this.buildRemoveForm(false);
    }

    public buildRemoveForm(isAssignType: boolean): void {
        const l_AgentUserFilterPayload = new AgentUserFilterPayload();
        l_AgentUserFilterPayload.agentID = this.selectedAgentId;

        this.agentService.getCompaniesForAgent(l_AgentUserFilterPayload).subscribe((response: AgentCompany[]) => {
            this.isAssign = isAssignType;
            this.buildcompanyForm(response);
        });
    }

    public buildAssignForm(): void {
        const l_AgentUserFilterPayload = new AgentUserFilterPayload();
        l_AgentUserFilterPayload.agentID = this.selectedAgentId;

        this.agentService.getCompaniesNotAssignedToAgent(l_AgentUserFilterPayload).subscribe((response: AgentCompany[]) => {
            this.isAssign = true;
            this.buildcompanyForm(response);
        });
    }

    private buildcompanyForm(agentCompanies: AgentCompany[]): void {
        if (agentCompanies) {
            this.companyForm = null;
            this.companyColumns = null;
            this.companyColumns = Object.assign([], this.baseCompanyColumns);
            this.companyColumns.push({
                propertyId: this.formSelectCheckbox,
                columnName: this.isAssign ? 'Assign' : 'Remove',
                columnType: TableColumnType.checkBox,
                width: "10%"
            });

            this.companyMatTableData = new MatTableDataSource<AgentCompany>();
            this.companyMatTableData.data = agentCompanies;

            let l_FormArray: FormArray = new FormArray([], FormValidationService.formArrayRequiresCheckboxChecked(this.formSelectCheckbox));
            agentCompanies.forEach((company: AgentCompany) => {
                l_FormArray.push(
                    this.formBuilder.group({
                        name: [company.companyName],
                        id: [company.companyID],
                        code: [company.code],
                        [this.formSelectCheckbox]: [false],
                    })
                );
            });

            this.companyForm = this.formBuilder.group({
                [this.companyFormArrayName]: l_FormArray,
            });
        } else {
            this.companyForm = null;
            this.companyMatTableData = null;
        }
    }

    public unassignCompanies(): void {
        const l_CompanyAgentsSelected = this.selectedCompanies();

        this.agentService.unAssignComapniesFromAgents(l_CompanyAgentsSelected).subscribe(() => {
            this.buildRemoveForm(false);
        });
    }

    // After assigning companies we need to build the form to unassign to display assigned companies to the agent
    public assignCompanies(): void {
        const l_CompanyAgentsSelected = this.selectedCompanies();

        this.agentService.assignCompaniesToAgents(l_CompanyAgentsSelected).subscribe(() => {
            this.buildRemoveForm(true);
        });
    }

    private selectedCompanies(): AgentCompaniesSelected {
        const l_AgentCompaniesSelected = new AgentCompaniesSelected();
        let l_SelectedCompanies = new Array<number>();

        const l_FormArray = this.companyForm.get(this.companyFormArrayName) as FormArray;
        l_FormArray.controls.map((cfa) => {
            if (cfa.get([this.formSelectCheckbox]).value)
                l_SelectedCompanies.push(cfa.get(['id']).value);
        });

        l_AgentCompaniesSelected.agentID = this.agentControlForm.get('agentID').value;
        l_AgentCompaniesSelected.companyIDs = l_SelectedCompanies;

        return l_AgentCompaniesSelected;
    }
}
